import { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useEmpresa } from '../contexts/EmpresaContext';
import { getProfile } from '../store/slices/authSlice';
import api from '../api/config';
import {
  User,
  Mail,
  Phone,
  MapPin,
  Building,
  Calendar,
  Edit,
  Save,
  X,
  Camera,
  Shield,
  Key,
  Bell,
  Globe,
  Waves,
  AlertCircle
} from 'lucide-react';
import '../styles/Perfil.css';

const Perfil = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [themeColor, setThemeColor] = useState('#c4fffc');
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const dispatch = useDispatch();

  // Obtener datos del usuario desde Redux
  const { user, isSuperAdmin } = useSelector(state => state.auth);
  const { empresaActiva } = useEmpresa();

  // Obtener el rol del usuario actual
  const currentUserRole = empresaActiva?.rolUsuario || empresaActiva?.role;
  const canEditRole = isSuperAdmin || currentUserRole === 'admin';

  // Función para determinar si un color es claro u oscuro
  const isLightColor = (color) => {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5;
  };

  // Actualizar color del tema
  useEffect(() => {
    const updateThemeColor = () => {
      const color = localStorage.getItem('theme-primary-color') || '#c4fffc';
      setThemeColor(color);
    };

    updateThemeColor();
    const interval = setInterval(updateThemeColor, 200);
    window.addEventListener('theme-color-change', updateThemeColor);

    return () => {
      clearInterval(interval);
      window.removeEventListener('theme-color-change', updateThemeColor);
    };
  }, []);

  const [userInfo, setUserInfo] = useState({
    nombre: '',
    apellido: '',
    email: '',
    telefono: '',
    empresa: '',
    cargo: '',
    departamento: '',
    systemRole: 'member'
  });
  const [originalUserInfo, setOriginalUserInfo] = useState(null);
  const [positions, setPositions] = useState([]);
  const [systemRoles, setSystemRoles] = useState([
    { id: 1, name: 'admin', description: 'Administrador - Puede gestionar todo en la empresa' },
    { id: 2, name: 'editor', description: 'Editor - Puede editar con algunas restricciones' },
    { id: 3, name: 'member', description: 'Miembro - Acceso básico con permisos personalizables' }
  ]);

  // Detectar si hay cambios en el perfil
  const hasChanges = useMemo(() => {
    if (!originalUserInfo) return false;
    return JSON.stringify(userInfo) !== JSON.stringify(originalUserInfo);
  }, [userInfo, originalUserInfo]);

  // Función para aclarar un color
  const lightenColor = (color, percent = 30) => {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    const newR = Math.min(255, Math.floor(r + (255 - r) * (percent / 100)));
    const newG = Math.min(255, Math.floor(g + (255 - g) * (percent / 100)));
    const newB = Math.min(255, Math.floor(b + (255 - b) * (percent / 100)));

    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
  };

  // Función para oscurecer un color (hacer más visible)
  const darkenColor = (color, percent = 40) => {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    const newR = Math.max(0, Math.floor(r * (1 - percent / 100)));
    const newG = Math.max(0, Math.floor(g * (1 - percent / 100)));
    const newB = Math.max(0, Math.floor(b * (1 - percent / 100)));

    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
  };

  // Cargar catálogos
  useEffect(() => {
    const loadCatalogs = async () => {
      if (empresaActiva) {
        try {
          const posRes = await api.get('/catalog/positions', { params: { companyId: empresaActiva.id } });
          setPositions(posRes.data.data || []);
        } catch (error) {
          console.error('Error loading catalogs:', error);
        }
      }
    };
    loadCatalogs();
  }, [empresaActiva]);

  // Cargar perfil desde API si no hay usuario en Redux
  useEffect(() => {
    if (!user) {
      console.log('📥 Cargando perfil desde API...');
      dispatch(getProfile());
    }
  }, [user, dispatch]);

  // Cargar datos del usuario desde el backend
  useEffect(() => {
    const loadUserProfile = async () => {
      // Para superadministradores, cargar datos básicos inmediatamente
      if (user && isSuperAdmin) {
        const superAdminData = {
          nombre: user.firstName || '',
          apellido: user.lastName || '',
          email: user.email || '',
          telefono: user.phone || '',
          empresa: '',
          cargo: '',
          departamento: '',
          systemRole: 'admin'
        };
        setUserInfo(superAdminData);
        setOriginalUserInfo(superAdminData);
        console.log('👑 Superadministrador - Datos cargados:', superAdminData);
        return;
      }

      // Para usuarios normales
      if (user && empresaActiva) {
        try {
          console.log('📥 Cargando perfil completo del usuario...');
          const response = await api.get('/team/members', {
            params: { companyId: empresaActiva.id }
          });

          if (response.data.success) {
            // Buscar el usuario actual en la lista de miembros
            const currentMember = response.data.data.find(m => m.id === user.id);

            if (currentMember) {
              console.log('✅ Perfil del usuario cargado:', currentMember);
              console.log('Position:', currentMember.position);
              console.log('Department:', currentMember.department);
              console.log('Phone:', currentMember.phone);
              console.log('SystemRole:', currentMember.systemRole);

              const profileData = {
                nombre: currentMember.firstName || '',
                apellido: currentMember.lastName || '',
                email: currentMember.email || '',
                telefono: currentMember.phone || '',
                empresa: empresaActiva?.nombreFantasia || empresaActiva?.tradeName || '',
                cargo: currentMember.position || '',
                departamento: currentMember.department || '',
                systemRole: currentMember.systemRole || 'member'
              };

              setUserInfo(profileData);
              setOriginalUserInfo(profileData);
              console.log('📋 UserInfo actualizado:', profileData);
            } else {
              // Fallback a datos básicos del usuario
              console.log('⚠️ Usuario no encontrado en team members, usando datos básicos');
              console.log('EmpresaActiva:', empresaActiva);

              const fallbackData = {
                nombre: user.firstName || '',
                apellido: user.lastName || '',
                email: user.email || '',
                telefono: '',
                empresa: empresaActiva?.nombreFantasia || empresaActiva?.tradeName || '',
                cargo: '',
                departamento: '',
                systemRole: empresaActiva?.rolUsuario || empresaActiva?.role || 'member'
              };

              setUserInfo(fallbackData);
              setOriginalUserInfo(fallbackData);
            }
          }
        } catch (error) {
          console.error('Error al cargar perfil:', error);
          // Fallback a datos básicos
          const errorFallbackData = {
            nombre: user.firstName || '',
            apellido: user.lastName || '',
            email: user.email || '',
            telefono: '',
            empresa: empresaActiva?.nombreFantasia || empresaActiva?.tradeName || '',
            cargo: '',
            departamento: '',
            systemRole: empresaActiva?.rolUsuario || empresaActiva?.role || 'member'
          };

          setUserInfo(errorFallbackData);
          setOriginalUserInfo(errorFallbackData);
        }
      }
    };

    loadUserProfile();
  }, [user, empresaActiva, isSuperAdmin]);

  const [notifications, setNotifications] = useState({
    emailOrdenes: true,
    emailFacturas: true,
    emailUrgencias: true,
    pushOrdenes: false,
    pushFacturas: true,
    pushUrgencias: true
  });

  const handleSave = async () => {
    try {
      // Validar que los campos requeridos no estén vacíos
      if (!userInfo.nombre || !userInfo.apellido || !userInfo.email) {
        alert('Por favor completa los campos obligatorios: Nombre, Apellido y Email');
        return;
      }

      // Preparar datos para enviar al backend (solo campos que existen en el modelo)
      const updateData = {
        firstName: userInfo.nombre.trim(),
        lastName: userInfo.apellido.trim(),
        email: userInfo.email.trim(),
        phone: userInfo.telefono?.trim() || '',
        department: userInfo.departamento?.trim() || ''
      };

      console.log('📤 Enviando actualización de perfil:', updateData);

      // Enviar actualización al backend
      const response = await api.put('/auth/profile', updateData);

      console.log('✅ Respuesta del servidor:', response.data);

      // Actualizar localStorage
      localStorage.setItem('username', userInfo.nombre);

      // Actualizar user en localStorage con los datos del servidor
      const updatedUser = response.data.user;
      localStorage.setItem('user', JSON.stringify(updatedUser));

      // Actualizar originalUserInfo para que hasChanges sea false
      setOriginalUserInfo({ ...userInfo });
      setIsEditing(false);

      // Mostrar mensaje de éxito
      alert('Perfil actualizado correctamente');

      // Recargar la página para reflejar los cambios en todos los componentes
      window.location.reload();
    } catch (error) {
      console.error('❌ Error al actualizar perfil:', error);
      console.error('❌ Detalle del error:', error.response?.data);
      alert('Error al actualizar el perfil: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleCancel = () => {
    // Restaurar datos originales guardados
    if (originalUserInfo) {
      setUserInfo({ ...originalUserInfo });
    }
    setIsEditing(false);
  };

  const handleInputChange = (field, value) => {
    setUserInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNotificationChange = (field, value) => {
    setNotifications(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validatePassword = (password) => {
    const minLength = 8;
    const maxLength = 20;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*]/.test(password);

    if (password.length < minLength || password.length > maxLength) {
      return 'La contraseña debe tener entre 8 y 20 caracteres';
    }
    if (!hasUpperCase) {
      return 'La contraseña debe contener al menos una mayúscula';
    }
    if (!hasLowerCase) {
      return 'La contraseña debe contener al menos una minúscula';
    }
    if (!hasNumbers) {
      return 'La contraseña debe contener al menos un número';
    }
    if (!hasSpecialChar) {
      return 'La contraseña debe contener al menos un símbolo (!@#$%^&*)';
    }
    return '';
  };

  const handlePasswordChange = async () => {
    setPasswordError('');

    // Validar que todos los campos estén llenos
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setPasswordError('Todos los campos son obligatorios');
      return;
    }

    // Validar que las contraseñas coincidan
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('Las contraseñas no coinciden');
      return;
    }

    // Validar formato de nueva contraseña
    const validationError = validatePassword(passwordData.newPassword);
    if (validationError) {
      setPasswordError(validationError);
      return;
    }

    try {
      await api.post('/auth/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      alert('Contraseña actualizada correctamente');
      setPasswordModalOpen(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      console.error('Error al cambiar contraseña:', error);
      setPasswordError(error.response?.data?.message || 'Error al cambiar la contraseña');
    }
  };

  return (
    <div className="perfil-container">
      <div className="perfil-header">
        <div className="header-title">
          <User size={28} />
          <h1>Mi Perfil</h1>
        </div>
        <div className="header-actions">
          {!isEditing ? (
            <button
              className="btn-edit"
              onClick={() => setIsEditing(true)}
              style={{
                background: themeColor,
                color: isLightColor(themeColor) ? '#000' : '#fff'
              }}
            >
              <Edit size={20} />
              Editar Perfil
            </button>
          ) : (
            <div className="edit-actions">
              <button
                className="btn-save"
                onClick={handleSave}
                disabled={!hasChanges}
                style={{
                  background: lightenColor(themeColor, 20),
                  color: isLightColor(themeColor) ? '#000' : '#fff',
                  border: '2px solid #000',
                  opacity: hasChanges ? 1 : 0.5,
                  cursor: hasChanges ? 'pointer' : 'not-allowed'
                }}
              >
                <Save size={20} />
                Guardar
              </button>
              <button className="btn-cancel" onClick={handleCancel}>
                <X size={20} />
                Cancelar
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="perfil-content">
        <div className="perfil-sidebar">
          <div className="profile-card">
            <div className="profile-avatar" style={{ background: themeColor }}>
              <Waves size={40} color={isLightColor(themeColor) ? '#000' : '#fff'} />
              {isEditing && (
                <button className="avatar-edit">
                  <Camera size={16} />
                </button>
              )}
            </div>
            <div className="profile-info">
              <h3>
                {userInfo.nombre && userInfo.apellido
                  ? `${userInfo.nombre} ${userInfo.apellido}`
                  : (userInfo.nombre || 'Cargando...')}
              </h3>
              <p className="profile-role" style={{
                color: darkenColor(themeColor, 40),
                fontWeight: '700'
              }}>
                {isSuperAdmin ? 'Superadministrador' : (userInfo.cargo || 'Sin cargo')}
              </p>
              {!isSuperAdmin && <p className="profile-company">{userInfo.empresa || 'Sin empresa'}</p>}
            </div>
          </div>

          <div className="profile-nav">
            <button
              className={`nav-item ${activeTab === 'general' ? 'active' : ''}`}
              onClick={() => setActiveTab('general')}
              style={activeTab === 'general' ? {
                background: themeColor,
                color: isLightColor(themeColor) ? '#000' : '#fff'
              } : {
                background: 'transparent',
                color: '#000'
              }}
            >
              <User size={20} />
              Información General
            </button>
            <button
              className={`nav-item ${activeTab === 'trabajo' ? 'active' : ''}`}
              onClick={() => setActiveTab('trabajo')}
              style={activeTab === 'trabajo' ? {
                background: themeColor,
                color: isLightColor(themeColor) ? '#000' : '#fff'
              } : {
                background: 'transparent',
                color: '#000'
              }}
            >
              <Building size={20} />
              Información Laboral
            </button>
            <button
              className={`nav-item ${activeTab === 'seguridad' ? 'active' : ''}`}
              onClick={() => setActiveTab('seguridad')}
              style={activeTab === 'seguridad' ? {
                background: themeColor,
                color: isLightColor(themeColor) ? '#000' : '#fff'
              } : {
                background: 'transparent',
                color: '#000'
              }}
            >
              <Shield size={20} />
              Seguridad
            </button>
          </div>
        </div>

        <div className="perfil-main">
          {activeTab === 'general' && (
            <div className="tab-content">
              <h2>Información General</h2>
              <div className="form-grid">
                <div className="form-group">
                  <label>Nombre</label>
                  <div className="input-with-icon has-border">
                    <User size={20} />
                    {isEditing ? (
                      <input
                        type="text"
                        value={userInfo.nombre}
                        onChange={(e) => handleInputChange('nombre', e.target.value)}
                      />
                    ) : (
                      <span>{userInfo.nombre}</span>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label>Apellido</label>
                  <div className="input-with-icon has-border">
                    <User size={20} />
                    {isEditing ? (
                      <input
                        type="text"
                        value={userInfo.apellido}
                        onChange={(e) => handleInputChange('apellido', e.target.value)}
                      />
                    ) : (
                      <span>{userInfo.apellido}</span>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label>Email</label>
                  <div className="input-with-icon has-border">
                    <Mail size={20} />
                    {isEditing ? (
                      <input
                        type="email"
                        value={userInfo.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                      />
                    ) : (
                      <span>{userInfo.email}</span>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label>Teléfono</label>
                  <div className="input-with-icon has-border">
                    <Phone size={20} />
                    {isEditing ? (
                      <input
                        type="tel"
                        value={userInfo.telefono}
                        onChange={(e) => handleInputChange('telefono', e.target.value)}
                      />
                    ) : (
                      <span>{userInfo.telefono}</span>
                    )}
                  </div>
                </div>

              </div>
            </div>
          )}

          {activeTab === 'trabajo' && (
            <div className="tab-content">
              <h2>Información Laboral</h2>
              <div className="form-grid">
                <div className="form-group">
                  <label>Empresa</label>
                  <div className="input-with-icon has-border">
                    <Building size={20} />
                    {isEditing ? (
                      <input
                        type="text"
                        value={userInfo.empresa}
                        onChange={(e) => handleInputChange('empresa', e.target.value)}
                      />
                    ) : (
                      <span>{userInfo.empresa}</span>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label>Cargo / Posición</label>
                  <div className="input-with-icon has-border">
                    <User size={20} />
                    {isEditing ? (
                      <select
                        value={userInfo.cargo}
                        onChange={(e) => handleInputChange('cargo', e.target.value)}
                      >
                        <option value="">Seleccione un cargo</option>
                        {positions.map(pos => (
                          <option key={pos.id} value={pos.name}>
                            {pos.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span>{userInfo.cargo || 'No especificado'}</span>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label>Departamento</label>
                  <div className="input-with-icon has-border">
                    <Building size={20} />
                    {isEditing ? (
                      <input
                        type="text"
                        value={userInfo.departamento}
                        onChange={(e) => handleInputChange('departamento', e.target.value)}
                        placeholder="Ej: Gerencia"
                      />
                    ) : (
                      <span>{userInfo.departamento || 'No especificado'}</span>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label>Rol de Permisos</label>
                  <div className="input-with-icon has-border">
                    <Shield size={20} />
                    {isEditing && canEditRole ? (
                      <select
                        value={userInfo.systemRole}
                        onChange={(e) => handleInputChange('systemRole', e.target.value)}
                        style={{
                          fontWeight: '600'
                        }}
                      >
                        {systemRoles.map(role => (
                          <option key={role.id} value={role.name}>
                            {role.description}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span style={{
                        padding: '8px 12px',
                        backgroundColor: userInfo.systemRole === 'admin' ? themeColor : userInfo.systemRole === 'editor' ? themeColor : '#e0e0e0',
                        color: '#000',
                        border: '2px solid #000',
                        borderRadius: '6px',
                        fontWeight: '600',
                        fontSize: '14px',
                        display: 'inline-block'
                      }}>
                        {userInfo.systemRole === 'admin' ? 'Administrador - Puede gestionar todo en la empresa' :
                         userInfo.systemRole === 'editor' ? 'Editor - Puede editar con algunas restricciones' :
                         'Miembro - Acceso básico con permisos personalizables'}
                      </span>
                    )}
                  </div>
                </div>

              </div>
            </div>
          )}

          {activeTab === 'seguridad' && (
            <div className="tab-content">
              <h2>Configuración de Seguridad</h2>
              <div className="security-section">
                <div className="security-item">
                  <div className="security-icon" style={{ background: themeColor }}>
                    <Key size={24} color={isLightColor(themeColor) ? '#000' : '#fff'} />
                  </div>
                  <div className="security-info">
                    <h3>Cambiar Contraseña</h3>
                    <p>Actualiza tu contraseña regularmente para mantener tu cuenta segura</p>
                    <button
                      className="btn-security"
                      onClick={() => setPasswordModalOpen(true)}
                      style={{
                        background: themeColor,
                        color: isLightColor(themeColor) ? '#000' : '#fff'
                      }}
                    >
                      Cambiar Contraseña
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Cambio de Contraseña */}
      {passwordModalOpen && ReactDOM.createPortal(
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '10px'
        }}>
          <div style={{
            backgroundColor: 'white',
            border: '3px solid #000',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '85vh',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '24px'
          }} onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 style={{ fontSize: '24px', fontWeight: '700', margin: 0, color: '#000', fontFamily: "'Space Grotesk', sans-serif" }}>
                Cambiar Contraseña
              </h2>
              <button
                onClick={() => setPasswordModalOpen(false)}
                onMouseEnter={(e) => {
                  e.target.style.background = themeColor;
                  e.target.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#f0f0f0';
                  e.target.style.transform = 'scale(1)';
                }}
                style={{
                  background: '#f0f0f0',
                  border: '2px solid #000',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  padding: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease'
                }}
              >
                <X size={24} color="#000" />
              </button>
            </div>

            {/* Requisitos */}
            <div style={{
              background: '#f8f9fa',
              border: '2px solid #000',
              borderRadius: '10px',
              padding: '16px'
            }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#000', fontFamily: "'Space Grotesk', sans-serif", fontWeight: '600' }}>
                <strong>La contraseña debe contener:</strong>
              </p>
              <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#000', fontFamily: "'Space Grotesk', sans-serif" }}>
                <li>8-20 caracteres</li>
                <li>Mayúsculas</li>
                <li>Minúsculas</li>
                <li>Números</li>
                <li>Símbolos (!@#$%^&*)</li>
              </ul>
            </div>

            {/* Error message */}
            {passwordError && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: '#ffebee',
                border: '2px solid #ef5350',
                borderRadius: '8px',
                padding: '12px 16px',
                color: '#c62828',
                fontSize: '14px',
                fontFamily: "'Space Grotesk', sans-serif"
              }}>
                <AlertCircle size={16} />
                {passwordError}
              </div>
            )}

            {/* Contraseña Actual */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '14px', fontWeight: '600', color: '#000', fontFamily: "'Space Grotesk', sans-serif" }}>
                Contraseña Actual
              </label>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 16px',
                border: '2px solid #000',
                borderRadius: '8px',
                background: 'white'
              }}>
                <Key size={20} color="#000" />
                <input
                  type="password"
                  placeholder="Ingresa tu contraseña actual"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  style={{
                    flex: 1,
                    border: 'none',
                    outline: 'none',
                    fontSize: '14px',
                    fontFamily: "'Space Grotesk', sans-serif",
                    color: '#000',
                    background: 'none'
                  }}
                />
              </div>
            </div>

            {/* Nueva Contraseña */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '14px', fontWeight: '600', color: '#000', fontFamily: "'Space Grotesk', sans-serif" }}>
                Nueva Contraseña
              </label>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 16px',
                border: '2px solid #000',
                borderRadius: '8px',
                background: 'white'
              }}>
                <Key size={20} color="#000" />
                <input
                  type="password"
                  placeholder="Ejemplo: MiClave@2024"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  style={{
                    flex: 1,
                    border: 'none',
                    outline: 'none',
                    fontSize: '14px',
                    fontFamily: "'Space Grotesk', sans-serif",
                    color: '#000',
                    background: 'none'
                  }}
                />
              </div>
            </div>

            {/* Confirmar Contraseña */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '14px', fontWeight: '600', color: '#000', fontFamily: "'Space Grotesk', sans-serif" }}>
                Confirmar Nueva Contraseña
              </label>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 16px',
                border: '2px solid #000',
                borderRadius: '8px',
                background: 'white'
              }}>
                <Key size={20} color="#000" />
                <input
                  type="password"
                  placeholder="Repetir contraseña"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  style={{
                    flex: 1,
                    border: 'none',
                    outline: 'none',
                    fontSize: '14px',
                    fontFamily: "'Space Grotesk', sans-serif",
                    color: '#000',
                    background: 'none'
                  }}
                />
              </div>
            </div>

            {/* Footer Buttons */}
            <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
              <button
                onClick={() => setPasswordModalOpen(false)}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  background: 'white',
                  color: '#000',
                  border: '2px solid #000',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontFamily: "'Space Grotesk', sans-serif"
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handlePasswordChange}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  background: themeColor,
                  color: isLightColor(themeColor) ? '#000' : '#fff',
                  border: '2px solid #000',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontFamily: "'Space Grotesk', sans-serif"
                }}
              >
                Cambiar Contraseña
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Perfil;